import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Button } from "@/components/ui/button";
import { DeliverablePreview } from "@/components/home/DeliverablePreview";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";

/**
 * Above-the-fold hero. Intentionally free of any JS-gated reveal wrapper —
 * the h1 is the LCP element and must paint on first render.
 */
export function Hero() {
  return (
    <Section
      tone="paper"
      eager
      className="relative overflow-hidden"
      style={{ paddingBlock: "clamp(72px, 8vw, 128px)" }}
    >
      <Aurora variant="light" />

      <Container className="relative">
        <div className="grid items-center gap-14 lg:grid-cols-[52fr_48fr] lg:gap-10">
          <div className="min-w-0">
            <Eyebrow className="laa-rise laa-rise-1 inline-flex items-center gap-2 rounded-full border border-brand-600/25 bg-brand-100 px-3.5 py-1.5 text-brand-700">
              <span className="size-1.5 rounded-full bg-brand-600" aria-hidden="true" />
              AI Strategy · Implementation · Adoption
            </Eyebrow>

            <h1
              className="laa-rise laa-rise-2 mt-6 max-w-[16ch] font-display text-display-xl text-ink-900 xl:max-w-[18ch]"
              style={{ textWrap: "balance" }}
            >
              Find where AI <span className="hl text-brand-700">actually pays</span> in your small
              business.
            </h1>

            <p className="laa-rise laa-rise-3 mt-6 max-w-[60ch] text-body-lg text-ink-600">
              Most AI initiatives stall because nobody mapped them to a real business problem first.
              We start with your operations,{" "}
              <strong className="font-semibold text-ink-900">
                rank where AI unlocks measurable value
              </strong>
              , and build the systems that get used.
            </p>

            <div className="laa-rise laa-rise-4 mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:flex-nowrap">
              <CtaButton asChild size="lg" className="whitespace-nowrap">
                <Link to="/book">Book a Free AI Call</Link>
              </CtaButton>
              <Button asChild variant="secondary" className="group whitespace-nowrap">
                <Link to="/tools/ai-opportunity-scorecard">
                  Take the AI Opportunity Scorecard
                  <ArrowRight className="transition-transform duration-150 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            <p className="laa-rise laa-rise-5 mt-5 text-body-sm text-ink-500">
              Free 20-minute call · No obligation · Slots most weekdays
            </p>
          </div>

          <div className="laa-rise laa-rise-6 w-full">
            <div className="mx-auto w-full max-w-[420px] lg:max-w-none">
              <DeliverablePreview />
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

export default Hero;
